import { Button } from '#app/components/Buttons/Button.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { css, styled } from 'styled-components';

/**
 * The top-level containing component
 */
export const Container = styled.div<{ actionsVisible?: boolean }>`
  ${({ actionsVisible = false, theme }) => css`
    position: relative;
    width: 100%;
    border-bottom: 1px solid transparent;

    ${theme.mediaQueries.lg} {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
      margin-top: ${actionsVisible ? 16 : 32}px;
    }
  `}
`;

export const Scroll = styled.div`
  ${({ theme }) => css`
    overflow-x: auto;
    box-shadow: inset 0px -1px ${theme.colorsV2.gray40};

    ${theme.mediaQueries.sm} {
      width: auto;
      overflow-x: unset;
    }
  `}
`;

export const List = styled.ul`
  ${({ theme }) => css`
    display: flex;
    flex: 1;
    flex-flow: row nowrap;
    justify-content: start;
    scroll-snap-type: x mandatory;
    gap: 24px;
    position: relative;
    border-bottom: 1px solid ${theme.colorsV2.gray30};

    > * {
      scroll-snap-align: start;
    }

    ${theme.mediaQueries.sm} {
      gap: 40px;
    }
  `}
`;

export const Tab = styled.li<{ isActiveTab?: boolean }>`
  ${({ isActiveTab, theme }) => css`
    color: ${isActiveTab ? theme.colorsV2.black : theme.colorsV2.gray50};
    text-align: left;
    position: relative;
  `}
`;

const tabContent = css`
  display: block;
  position: relative;
`;

export const LockedTabContent = styled.span`
  ${tabContent}
  cursor: default;
  white-space: nowrap;
`;

export const TabLink = styled(Link)`
  ${tabContent}
  height: 100%;
`;

export const TabLinkContent = styled.div<{
  isActiveTab: boolean;
}>`
  ${({ isActiveTab, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 6px;
    height: 100%;

    ${isActiveTab &&
    css`
      position: relative;
      &:after {
        content: '';
        position: absolute;
        width: 100%;
        background-color: ${theme.colorsV2.black};
        height: 2px;
        left: 0;
        bottom: -2px;
        z-index: 1;
      }
      ${theme.mediaQueries.lg} {
        &:after {
          display: none;
        }
      }
    `}
  `}
`;

export const Actions = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
    margin-top: 16px;

    & > button {
      width: 100%;
    }

    ${theme.mediaQueries.lg} {
      margin-top: 0;
      margin-bottom: 16px;
    }
  `}
`;

export const ActionButton = styled(Button)`
  ${({ theme }) => css`
    margin-left: 0;
    width: 343px;

    ${theme.mediaQueries.sm} {
      width: auto;
      margin-left: 16px;
    }
  `}
`;

export const TabText = styled(Text).attrs({
  as: 'span',
  textStyle: 'sixthLevelHeadline',
})`
  ${({ theme }) => css`
    font-size: 1rem;
    line-height: 1.5;
    white-space: nowrap;

    ${theme.mediaQueries.sm} {
      font-size: ${theme.textStyles.sixthLevelHeadline.fontSize};
      line-height: ${theme.textStyles.sixthLevelHeadline.lineHeight};
    }

    > * {
      vertical-align: middle;
    }
  `}
`;

export const TabIconContainer = styled.div`
  margin-right: 8px;
  height: 16px;
`;

export const AnalyticsTabContainer = styled.span`
  display: flex;
  align-items: center;

  & > svg {
    margin-right: 4px;
  }
`;
